import { useEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import {
  hallPatrolQuickActions,
  type HallPatrolAction,
  type HallPatrolTankSnapshot,
} from "./hallPatrol";
import { hallPatrolM2Surface, workerOperationKindForHallPatrolAction } from "./hallPatrolM2Bridge";
import { startContinuousQrScanner, type ContinuousQrScannerHandle } from "./continuousScanner";
import { HallPatrolScannerSession } from "./scannerSession";
import { loadHallPatrolTankSnapshots } from "./liveTankSnapshots";
import { resolveTankQrLocally } from "./localTankResolver";
import { readHallPatrolTankCache, writeHallPatrolTankCache } from "./tankSnapshotCache";
import { supabase } from "../auth/supabaseClient";
import type { OutboxSummary } from "../sync/types";
import { BreedingActionPanel } from "../ux/BreedingActionPanel";
import { FeedingPanel } from "../ux/FeedingPanel";
import { TodayTaskCompletionPanel } from "../ux/TodayTaskCompletionPanel";
import { WorkerHallOperationsPanel } from "../ux/WorkerHallOperationsPanel";
import type { WorkItem } from "../ux/model";

interface HallPatrolLivePanelProps {
  farmId: string;
  userId: string;
  items: readonly WorkItem[];
  onSyncSummary?: (summary: OutboxSummary) => void;
  onTaskStatus?: (taskId: string, status: "done" | "conflict", summary: OutboxSummary) => void;
}

type ScanState =
  | { kind: "idle" }
  | { kind: "scanning" }
  | { kind: "resolved"; tank: HallPatrolTankSnapshot }
  | { kind: "invalid"; message: string }
  | { kind: "error"; message: string };

const ACTION_LABELS: Record<string, string> = {
  feeding_record: "ثبت غذا",
  spawn_record: "ثبت تخم‌ریزی",
  egg_result_record: "نتیجه تخم",
  free_swim_record: "شنای آزاد",
  parent_feeding_record: "تغذیه از والدین",
  parent_separation_record: "جداسازی والدین",
  growout_record: "انتقال به گله رشد",
  water_measurement: "اندازه‌گیری آب",
  water_change: "تعویض آب",
  siphon: "سیفون",
  treatment: "درمان",
  mortality: "تلفات",
  health_observation: "مشاهده سلامت",
  task_complete: "تکمیل کار",
};

export function HallPatrolLivePanel({
  farmId,
  userId,
  items,
  onSyncSummary,
  onTaskStatus,
}: HallPatrolLivePanelProps) {
  const [tanks, setTanks] = useState<HallPatrolTankSnapshot[]>([]);
  const tanksRef = useRef<HallPatrolTankSnapshot[]>([]);
  const [scan, setScan] = useState<ScanState>({ kind: "idle" });
  const [source, setSource] = useState<"cache" | "live" | "empty">("empty");
  const [activeAction, setActiveAction] = useState<HallPatrolAction>();
  const scannerRef = useRef<ContinuousQrScannerHandle | null>(null);
  const scannerSessionRef = useRef(new HallPatrolScannerSession());
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    tanksRef.current = tanks;
  }, [tanks]);

  useEffect(() => {
    let cancelled = false;

    void readHallPatrolTankCache(farmId).then((cached) => {
      if (cancelled || !cached?.length) return;
      setTanks(cached);
      setSource("cache");
    });

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return () => {
        cancelled = true;
      };
    }

    void loadHallPatrolTankSnapshots(supabase, { farmId, userId })
      .then(async (live) => {
        if (cancelled) return;
        setTanks(live);
        setSource("live");
        await writeHallPatrolTankCache(farmId, live).catch(() => undefined);
      })
      .catch(() => {
        // Cached snapshots remain usable when live refresh fails.
      });

    return () => {
      cancelled = true;
    };
  }, [farmId, userId]);

  useEffect(
    () => () => {
      void scannerRef.current?.stop();
      scannerRef.current = null;
    },
    [],
  );

  const actions = useMemo(
    () => (scan.kind === "resolved" ? hallPatrolQuickActions(scan.tank) : []),
    [scan],
  );
  const m2Actions = useMemo(
    () => actions.filter((action) => hallPatrolM2Surface(action.action) !== "deferred"),
    [actions],
  );
  const activeSurface = activeAction ? hallPatrolM2Surface(activeAction) : undefined;
  const activeWorkerOperation = activeAction
    ? workerOperationKindForHallPatrolAction(activeAction)
    : undefined;
  const activeTaskIds =
    scan.kind === "resolved"
      ? (scan.tank.pendingTasks ?? [])
          .filter((task) => task.assignedToCurrentActor)
          .map((task) => task.taskId)
      : [];

  async function stopScannerCamera() {
    await scannerRef.current?.stop().catch(() => undefined);
    scannerRef.current = null;
  }

  async function stopScanner() {
    await stopScannerCamera();
    scannerSessionRef.current.releaseAction();
    setActiveAction(undefined);
    setScan({ kind: "idle" });
  }

  async function startScanner() {
    await scannerRef.current?.stop().catch(() => undefined);
    scannerRef.current = null;
    scannerSessionRef.current = new HallPatrolScannerSession();
    setActiveAction(undefined);
    setScan({ kind: "scanning" });

    try {
      scannerRef.current = await startContinuousQrScanner({
        ...(Capacitor.isNativePlatform() || !videoRef.current
          ? {}
          : { videoElement: videoRef.current }),
        onPayload(rawValue) {
          const decision = scannerSessionRef.current.consider(rawValue, Date.now());

          if (decision.kind === "locked" || decision.kind === "duplicate") {
            return;
          }

          if (decision.kind === "invalid") {
            setScan({ kind: "invalid", message: "QR معتبر تانک تشخیص داده نشد." });
            return;
          }

          const resolution = resolveTankQrLocally(decision.tankCode, tanksRef.current);

          if (resolution.kind === "resolved") {
            setActiveAction(undefined);
            setScan({ kind: "resolved", tank: resolution.tank });
            void stopScannerCamera();
            return;
          }

          scannerSessionRef.current.releaseAction();

          if (resolution.kind === "not_found") {
            setScan({
              kind: "invalid",
              message: `تانک ${resolution.tankCode} در snapshot محلی این سالن پیدا نشد.`,
            });
            return;
          }

          if (resolution.kind === "ambiguous") {
            setScan({
              kind: "invalid",
              message: `کد ${resolution.tankCode} در snapshot محلی تکراری است.`,
            });
            return;
          }

          setScan({ kind: "invalid", message: "QR معتبر تانک تشخیص داده نشد." });
        },
        onError(message) {
          setScan({ kind: "error", message });
        },
      });
    } catch (error) {
      setScan({
        kind: "error",
        message: error instanceof Error ? error.message : "QR_SCANNER_FAILED",
      });
    }
  }

  return (
    <section
      className="section-card hall-patrol-preview hall-patrol-scanner-modal"
      aria-labelledby="patrol-live-title"
    >
      <div className="section-title-row">
        <h2 id="patrol-live-title">گشت سالن</h2>
        <span className="muted-chip">
          {source === "live" ? "● آنلاین" : source === "cache" ? "● آفلاین" : "در انتظار"}
        </span>
      </div>

      {!Capacitor.isNativePlatform() ? (
        <video
          ref={videoRef}
          muted
          playsInline
          style={{ width: "100%", minHeight: 180, borderRadius: 16 }}
        />
      ) : null}

      {scan.kind !== "resolved" ? (
        <div className="hall-patrol-controls">
          {scan.kind === "scanning" ? (
            <div className="scanner-live-indicator" aria-live="polite">
              <strong>دوربین فعال است</strong>
              <small>QR تانک را مقابل دوربین نگه دارید</small>
            </div>
          ) : (
            <button type="button" className="patrol-button" onClick={() => void startScanner()}>
              <span aria-hidden="true">▣</span>
              <span>
                <strong>شروع اسکن</strong>
                <small>QR تانک را جلوی دوربین بگیر</small>
              </span>
            </button>
          )}
          {scan.kind !== "idle" ? (
            <button type="button" className="secondary-button" onClick={() => void stopScanner()}>
              بستن دوربین
            </button>
          ) : null}
        </div>
      ) : null}

      {scan.kind === "resolved" ? (
        <div className="work-list" aria-live="polite">
          <div className="work-item pending">
            <span className="work-main">
              <strong>تانک {scan.tank.tankCode}</strong>
              <span>{scan.tank.active ? "فعال" : "غیرفعال"}</span>
            </span>
          </div>

          <div className="scanner-status-card">
            <span>تانک قفل شد؛ اکنون عملیات را انتخاب کنید.</span>
            <button type="button" className="text-button" onClick={() => void startScanner()}>
              اسکن تانک بعدی
            </button>
          </div>

          <div className="quick-grid">
            {m2Actions.length ? (
              m2Actions.map((action) => (
                <button
                  type="button"
                  className={
                    activeAction === action.action ? "quick-button active" : "quick-button"
                  }
                  key={action.action}
                  onClick={() => setActiveAction(action.action)}
                >
                  <strong>{ACTION_LABELS[action.action] ?? action.action}</strong>
                </button>
              ))
            ) : (
              <span className="muted-chip">اکشن عملیاتی مجاز برای این تانک وجود ندارد.</span>
            )}
          </div>

          {activeSurface === "worker" && activeWorkerOperation ? (
            <WorkerHallOperationsPanel
              farmId={farmId}
              userId={userId}
              initialTankId={scan.tank.tankId}
              initialOperation={activeWorkerOperation}
              {...(onSyncSummary ? { onSyncSummary } : {})}
            />
          ) : null}

          {activeSurface === "feeding" ? (
            <FeedingPanel
              farmId={farmId}
              userId={userId}
              preferredTankId={scan.tank.tankId}
              {...(onSyncSummary ? { onSyncSummary } : {})}
            />
          ) : null}

          {activeSurface === "breeding" ? (
            <BreedingActionPanel
              farmId={farmId}
              userId={userId}
              tank={scan.tank}
              action={activeAction ?? "spawn_record"}
              {...(onSyncSummary ? { onSyncSummary } : {})}
              onCompleted={() => {
                void loadHallPatrolTankSnapshots(supabase, { farmId, userId })
                  .then(async (live) => {
                    setTanks(live);
                    const refreshed = live.find((tank) => tank.tankId === scan.tank.tankId);
                    if (refreshed) {
                      setScan({ kind: "resolved", tank: refreshed });
                    }
                    await writeHallPatrolTankCache(farmId, live).catch(() => undefined);
                  })
                  .catch(() => undefined);
              }}
            />
          ) : null}

          {activeSurface === "task" ? (
            <TodayTaskCompletionPanel
              farmId={farmId}
              userId={userId}
              items={items}
              preferredTaskIds={activeTaskIds}
              {...(onSyncSummary ? { onSyncSummary } : {})}
              {...(onTaskStatus ? { onTaskStatus } : {})}
            />
          ) : null}
        </div>
      ) : null}

      {scan.kind === "invalid" || scan.kind === "error" ? (
        <p className="settings-warning" aria-live="polite">
          {scan.message}
        </p>
      ) : null}
    </section>
  );
}
